import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { useI18n } from '../../i18n/I18nProvider';
import { createJsonFetch, createPrintTemplate, listPrintTemplates } from './settings.api';
import type { JsonFetch, PrintTemplate } from './settings.api';

function useScreenFetch(): JsonFetch {
  const { authorizedFetch } = useAuth();
  return useMemo(() => createJsonFetch(authorizedFetch), [authorizedFetch]);
}

function BlockList({ template }: { template: PrintTemplate }): JSX.Element {
  return (
    <ul className="settings__blocks">
      {template.layout.blocks.map((block, index) => (
        <li key={`${block.type}-${String(index)}`}>
          <span className="muted">{block.type}</span>
          {block.text === undefined ? null : <span> {block.text}</span>}
          {block.label === undefined ? null : <span> {block.label}</span>}
        </li>
      ))}
    </ul>
  );
}

export function PrintTemplatesView({
  templates,
  error,
  onSelect,
}: {
  templates: readonly PrintTemplate[];
  error?: string | null;
  onSelect?: (id: string) => void;
}): JSX.Element {
  const { t, language } = useI18n();
  const selected = templates[0];
  return (
    <div className="settings__panel" data-testid="settings-print-templates">
      {error === null || error === undefined ? null : <p className="alert alert--danger">{error}</p>}
      {templates.length === 0 ? <p className="muted">{t('settings.empty')}</p> : null}
      <table className="table">
        <thead>
          <tr>
            <th>{t('printTemplates.name')}</th>
            <th>{t('printTemplates.documentType')}</th>
            <th>{t('printTemplates.locale')}</th>
            <th>{t('printTemplates.default')}</th>
          </tr>
        </thead>
        <tbody>
          {templates.map((template) => (
            <tr key={template.id} data-testid="print-template" data-locale={template.locale} data-template={template.id}>
              <td>
                <button type="button" className="link" onClick={() => onSelect?.(template.id)}>
                  {template.nameI18n[language]}
                </button>
              </td>
              <td className="mono">{template.documentType}</td>
              <td>{template.locale.toUpperCase()}</td>
              <td>{template.isDefault ? t('settings.users.yes') : t('settings.users.no')}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {selected === undefined ? null : (
        <article className="settings__card" data-testid="print-preview" data-template={selected.id}>
          <header className="settings__card-head">
            <strong>{selected.nameI18n[language]}</strong>
            <span className="pill">{t('printTemplates.preview')}</span>
          </header>
          <BlockList template={selected} />
        </article>
      )}
    </div>
  );
}

const DEFAULT_LAYOUT = '[{"type":"title","text":"Document"},{"type":"signature","label":"signature"}]';

export default function PrintTemplatesScreen(): JSX.Element {
  const { t } = useI18n();
  const fetchJson = useScreenFetch();
  const [templates, setTemplates] = useState<PrintTemplate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [documentType, setDocumentType] = useState('');
  const [name, setName] = useState('');
  const [locale, setLocale] = useState<'az' | 'en' | 'ru'>('az');
  const [layout, setLayout] = useState(DEFAULT_LAYOUT);

  const reload = useCallback(async (): Promise<void> => {
    try {
      setTemplates(await listPrintTemplates(fetchJson));
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  }, [fetchJson]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const save = async (): Promise<void> => {
    try {
      const blocks = JSON.parse(layout) as PrintTemplate['layout']['blocks'];
      await createPrintTemplate(fetchJson, {
        documentType,
        nameI18n: { az: name, en: name, ru: name },
        locale,
        layout: { blocks },
      });
      await reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  };

  if (templates === null) {
    return (
      <section className="card" data-testid="print-templates-loading">
        <p className="muted">{t('settings.loading')}</p>
      </section>
    );
  }

  return (
    <div className="settings__screen">
      <header className="page-head">
        <div>
          <h1>{t('printTemplates.title')}</h1>
          <p className="muted">{t('printTemplates.subtitle')}</p>
        </div>
      </header>

      <PrintTemplatesView templates={templates} error={error} />

      <section className="card" data-testid="print-editor">
        <h2>{t('printTemplates.editor')}</h2>
        <div className="settings__form">
          <label className="field">
            <span>{t('printTemplates.documentType')}</span>
            <input className="input" value={documentType} onChange={(event) => setDocumentType(event.currentTarget.value)} />
          </label>
          <label className="field">
            <span>{t('printTemplates.name')}</span>
            <input className="input" value={name} onChange={(event) => setName(event.currentTarget.value)} />
          </label>
          <label className="field">
            <span>{t('printTemplates.locale')}</span>
            <select
              className="input"
              value={locale}
              onChange={(event) => setLocale(event.currentTarget.value as 'az' | 'en' | 'ru')}
            >
              <option value="az">{t('printTemplates.langAz')}</option>
              <option value="en">{t('printTemplates.langEn')}</option>
              <option value="ru">{t('printTemplates.langRu')}</option>
            </select>
          </label>
          <label className="field settings__form-wide">
            <span>{t('printTemplates.layout')}</span>
            <textarea className="input settings__editor" value={layout} onChange={(event) => setLayout(event.currentTarget.value)} />
          </label>
        </div>
        <div className="settings__actions">
          <button type="button" className="btn btn--primary" onClick={() => void save()}>
            {t('printTemplates.save')}
          </button>
        </div>
      </section>
    </div>
  );
}
